/**
 * 🔔 useNotifications Hook
 * 
 * Real-time bildiriş idarəetməsi üçün hook.
 * SignalR ilə inteqrasiya olunub.
 * 
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import * as signalR from '@microsoft/signalr';
import { HerracContext } from '../../App';
import NotificationService from '../services/NotificationService';

const API_BASE_URL = 'https://api.herracim.az';

/**
 * Bildiriş hook-u
 * 
 * @param {Object} options - Konfiqurasiya
 * @param {boolean} options.autoConnect - Avtomatik qoşulma (default: true)
 * @param {number} options.pollingInterval - Polling intervalı ms (default: 60000)
 * @param {Function} options.onNewNotification - Yeni bildiriş callback
 * @returns {Object} - Bildiriş state və metodları
 */
export const useNotifications = (options = {}) => {
    const {
        autoConnect = true,
        pollingInterval = 60000,
        onNewNotification,
    } = options;

    const { user } = useContext(HerracContext);

    // State
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionState, setConnectionState] = useState('disconnected');

    // Refs
    const connectionRef = useRef(null);
    const pollingRef = useRef(null);
    const mountedRef = useRef(true);

    // ═══════════════════════════════════════════════════════════════
    // 📥 BİLDİRİŞLƏRİ YÜKLƏ
    // ═══════════════════════════════════════════════════════════════

    const fetchNotifications = useCallback(async (showLoading = true) => {
        if (!user?.userId) return;

        if (showLoading) setLoading(true);
        setError(null);

        try {
            const result = await NotificationService.getNotifications({ pageSize: 50 });
            
            if (result.success && mountedRef.current) {
                setNotifications(result.data.notifications);
                setUnreadCount(result.data.unreadCount);
            }
        } catch (err) {
            if (mountedRef.current) {
                setError('Bildirişlər yüklənə bilmədi');
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    }, [user?.userId]);

    const fetchUnreadCount = useCallback(async () => {
        if (!user?.userId) return;

        try {
            const result = await NotificationService.getUnreadCount();
            
            if (result.success && mountedRef.current) {
                setUnreadCount(result.unreadCount);
            }
        } catch (err) {
            console.error('Unread count fetch error:', err);
        }
    }, [user?.userId]);

    // ═══════════════════════════════════════════════════════════════
    // 🔌 SIGNALR BAĞLANTISI
    // ═══════════════════════════════════════════════════════════════

    const connectSignalR = useCallback(async () => {
        if (!user?.userId || connectionRef.current) return;

        try {
            setConnectionState('connecting');

            const connection = new signalR.HubConnectionBuilder()
                .withUrl(`${API_BASE_URL}/notificationHub`, {
                    accessTokenFactory: () => {
                        // Token varsa buradan qaytar
                        return localStorage.getItem('token') || '';
                    },
                    skipNegotiation: true,
                    transport: signalR.HttpTransportType.ServerSentEvents,
                })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
                .configureLogging(signalR.LogLevel.Warning)
                .build();

            // Event handlers
            connection.on('ReceiveNotification', (notification) => {
                if (mountedRef.current) {
                    setNotifications(prev => [notification, ...prev]);
                    setUnreadCount(prev => prev + 1);
                    
                    // Callback
                    onNewNotification?.(notification);

                    // Browser notification (əgər icazə varsa)
                    showBrowserNotification(notification);
                }
            });

            connection.on('NotificationRead', (notificationId) => {
                if (mountedRef.current) {
                    setNotifications(prev =>
                        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
                    );
                }
            });

            connection.on('Pong', () => {
                console.log('SignalR Pong received');
            });

            connection.onreconnecting(() => {
                setConnectionState('reconnecting');
                setIsConnected(false);
            });

            connection.onreconnected(() => {
                setConnectionState('connected');
                setIsConnected(true);
                fetchUnreadCount();
            });

            connection.onclose(() => {
                setConnectionState('disconnected');
                setIsConnected(false);
            });

            await connection.start();
            
            connectionRef.current = connection;
            setIsConnected(true);
            setConnectionState('connected');
            
            console.log('SignalR notification connection established');
        } catch (err) {
            console.error('SignalR connection error:', err);
            setConnectionState('error');
            setIsConnected(false);
        }
    }, [user?.userId, onNewNotification, fetchUnreadCount]);

    const disconnectSignalR = useCallback(async () => {
        if (connectionRef.current) {
            try {
                await connectionRef.current.stop();
            } catch (err) {
                console.error('SignalR disconnect error:', err);
            }
            connectionRef.current = null;
            setIsConnected(false);
            setConnectionState('disconnected');
        }
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // 🔔 BROWSER BİLDİRİŞİ
    // ═══════════════════════════════════════════════════════════════

    const showBrowserNotification = useCallback((notification) => {
        if ('Notification' in window && Notification.permission === 'granted') {
            const icon = NotificationService.getNotificationIcon(notification.type);
            
            new Notification(notification.title || 'Yeni Bildiriş', {
                body: notification.message,
                icon: '/favicon.ico',
                tag: `notification-${notification.id}`,
                badge: icon,
            });
        }
    }, []);

    const requestNotificationPermission = useCallback(async () => {
        if ('Notification' in window && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return Notification.permission === 'granted';
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // ✅ BİLDİRİŞ ƏMƏLİYYATLARI
    // ═══════════════════════════════════════════════════════════════

    const markAsRead = useCallback(async (notificationId) => {
        const result = await NotificationService.markAsRead(notificationId);
        
        if (result.success) {
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        }
        
        return result;
    }, []);

    const markAllAsRead = useCallback(async () => {
        const result = await NotificationService.markAllAsRead();
        
        if (result.success) {
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        }
        
        return result;
    }, []);

    const deleteNotification = useCallback(async (notificationId) => {
        const notification = notifications.find(n => n.id === notificationId);
        const result = await NotificationService.deleteNotification(notificationId);
        
        if (result.success) {
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            if (notification && !notification.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        }
        
        return result;
    }, [notifications]);

    const clearAll = useCallback(async () => {
        const result = await NotificationService.clearAllNotifications();
        
        if (result.success) {
            setNotifications([]);
            setUnreadCount(0);
        }
        
        return result;
    }, []);

    // ═══════════════════════════════════════════════════════════════
    // 🔄 EFFECTS
    // ═══════════════════════════════════════════════════════════════

    // Mount/Unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // İlkin yükləmə
    useEffect(() => {
        if (user?.userId) {
            fetchNotifications();
        }
    }, [user?.userId, fetchNotifications]);

    // SignalR bağlantısı
    useEffect(() => {
        if (autoConnect && user?.userId) {
            connectSignalR();
        }

        return () => {
            disconnectSignalR();
        };
    }, [autoConnect, user?.userId, connectSignalR, disconnectSignalR]);

    // Polling (fallback)
    useEffect(() => {
        if (!user?.userId) return;

        pollingRef.current = setInterval(() => {
            if (!isConnected) {
                fetchUnreadCount();
            }
        }, pollingInterval);

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, [user?.userId, isConnected, pollingInterval, fetchUnreadCount]);

    // ═══════════════════════════════════════════════════════════════
    // 📤 RETURN
    // ═══════════════════════════════════════════════════════════════

    return {
        // Data
        notifications,
        unreadCount,
        loading,
        error,

        // Connection
        isConnected,
        connectionState,

        // Actions
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAll,
        
        // Connection
        connect: connectSignalR,
        disconnect: disconnectSignalR,
        
        // Browser notifications
        requestNotificationPermission,

        // Helpers
        hasUnread: unreadCount > 0,
        isEmpty: notifications.length === 0,
    };
};

/**
 * Sadə unread count hook-u (Header üçün)
 */
export const useUnreadNotificationCount = () => {
    const { user } = useContext(HerracContext);
    const [count, setCount] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!user?.userId) return;

        const fetchCount = async () => {
            const result = await NotificationService.getUnreadCount();
            if (result.success) {
                setCount(result.unreadCount);
            }
        };

        fetchCount();
        intervalRef.current = setInterval(fetchCount, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [user?.userId]);

    return count;
};

export default useNotifications;